import React, { useEffect, useId, useRef, useState } from "react";

import { Modal } from "../../../common/modal";
import { OverlayPreloader } from "../../../common/preloader";
import { Notification } from "../../../common/notification";
import { ReactComponent as FollowingRemoveIcon } from "../../../../media/icons/following-remove.svg";
import styles from "./followingModal.module.css";

/**
 * "Manage" modal for what a member follows: remove + confirmation + toast (unchanged), and a
 * secondary "Visit public site" link per row. Renaming a followed item was removed (GROWTH-7130,
 * confusing since nothing used the custom name) — this is the only change from the modal's
 * previous shape, which also had rename via `updateFollowing`.
 */
const FollowingModal = ({ onClose, removePreloader, followings, removeFollowing, onSuccess }) => {
  const [pendingRemoveId, setPendingRemoveId] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const confirmTitleId = useId();
  const listRef = useRef(null);
  const confirmCardRef = useRef(null);
  const noButtonRef = useRef(null);
  // The Remove button that opened the confirmation; focus goes back to it when the confirmation closes.
  const removeTriggerRef = useRef(null);
  const wasConfirmingRef = useRef(false);
  const wasRemovingRef = useRef(false);

  // The confirmation is a part of the shared Modal's one dialog, not a dialog of its own, so it moves focus itself.
  // Closing it returns focus to the Remove button, or to the list when that row is gone (after a successful remove).
  useEffect(() => {
    const isConfirming = pendingRemoveId !== null;

    if (isConfirming) {
      noButtonRef.current?.focus();
    } else if (wasConfirmingRef.current) {
      (removeTriggerRef.current?.isConnected ? removeTriggerRef.current : listRef.current)?.focus();
    }

    wasConfirmingRef.current = isConfirming;
  }, [pendingRemoveId]);

  // Yes and No are disabled while the remove runs, and a browser drops focus from a disabled button to <body>, so the
  // card holds it. A failed remove leaves the confirmation open, and focus goes back to No.
  useEffect(() => {
    if (isRemoving) {
      confirmCardRef.current?.focus();
    } else if (wasRemovingRef.current && pendingRemoveId !== null) {
      noButtonRef.current?.focus();
    }

    wasRemovingRef.current = isRemoving;
  }, [isRemoving, pendingRemoveId]);

  // The failure message belongs to the confirmation it was raised in, so closing one clears it.
  const closeConfirm = () => {
    setPendingRemoveId(null);
    setErrorText(null);
  };

  const handleRemoveConfirmed = () => {
    if (pendingRemoveId === null || isRemoving) return;

    setIsRemoving(true);
    setErrorText(null);
    const idToRemove = pendingRemoveId;

    Promise.resolve(removeFollowing(idToRemove))
      .then(() => {
        if (typeof onSuccess === "function") {
          onSuccess("List removed successfully");
        }
        removeTriggerRef.current = null;
        closeConfirm();
      })
      .catch(() => {
        setErrorText("Failed to remove following item");
      })
      .finally(() => {
        setIsRemoving(false);
      });
  };

  return (
    <Modal
      clearAllModals={onClose}
      closeModal={onClose}
      classNameMain={styles.modal}
      classNameButton={pendingRemoveId !== null ? styles.modalCloseHidden : null}
    >
      {removePreloader && <OverlayPreloader />}

      <div className={styles.header}>
        <h3 className={styles.title}>Following</h3>
        <div className={styles.subtitle}>Following List:</div>
      </div>

      {/* Inert while the confirmation covers it, so Tab and a screen reader cannot reach the rows behind. */}
      <div className={styles.list} ref={listRef} tabIndex={-1} inert={pendingRemoveId !== null}>
        {followings.map(item => (
          <div className={styles.row} key={item.user_following_id || item.name}>
            <div className={styles.rowName}>{item.name}</div>
            <div className={styles.actions}>
              {item.url ? (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className={styles.visitLink}>
                  Visit site
                </a>
              ) : null}
              <button
                type="button"
                className={styles.actionButton}
                aria-label="Remove following"
                onClick={event => {
                  removeTriggerRef.current = event.currentTarget;
                  setPendingRemoveId(item.user_following_id);
                }}
              >
                <FollowingRemoveIcon />
              </button>
            </div>
          </div>
        ))}
      </div>

      {pendingRemoveId !== null ? (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmCard} role="group" aria-labelledby={confirmTitleId} ref={confirmCardRef} tabIndex={-1}>
            <button
              type="button"
              className={styles.confirmClose}
              aria-label="Close confirmation"
              onClick={closeConfirm}
              disabled={isRemoving}
            >
              ×
            </button>
            <div id={confirmTitleId} className={styles.confirmTitle}>
              Confirmation
            </div>
            <div className={styles.confirmText}>Are you sure you want to remove these?</div>
            {/* Inside the card: the confirmation stays open after a failure and its overlay would cover a message above it. */}
            {errorText ? (
              <Notification inline error close={() => setErrorText(null)} time={5000}>
                {errorText}
              </Notification>
            ) : null}
            <div className={styles.confirmActions}>
              <button type="button" className={styles.confirmYes} onClick={handleRemoveConfirmed} disabled={isRemoving}>
                Yes
              </button>
              <button type="button" className={styles.confirmNo} ref={noButtonRef} onClick={closeConfirm} disabled={isRemoving}>
                No
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default FollowingModal;
